import axios from 'axios';
import { getApiBaseUrl } from '../../utils/apiClient';

/**
 * Per-user "What's new" state: the server record (GET/POST /api/whats-new/*)
 * merged with a localStorage copy, so a failed call never re-shows a popup
 * someone already dismissed on this device. Every storage access is wrapped:
 * private windows and locked-down tablets can throw.
 */

// Keyed per login so two people sharing a bench keep their own record. The
// last login seen on this device is remembered for when the server is down.
const LOCAL_PREFIX = 'whatsNew:v1:';
const LAST_EMAIL_KEY = 'whatsNew:v1:lastEmail';
let currentEmail = null;

function localKey() {
  if (!currentEmail) {
    try { currentEmail = window.localStorage.getItem(LAST_EMAIL_KEY) || null; } catch (_) { currentEmail = null; }
  }
  return LOCAL_PREFIX + (currentEmail || 'anon');
}

function rememberEmail(email) {
  const clean = String(email || '').trim().toLowerCase();
  if (!clean) return;
  currentEmail = clean;
  try { window.localStorage.setItem(LAST_EMAIL_KEY, clean); } catch (_) { /* blocked storage */ }
}

/** Tests only. */
export function resetWhatsNewLocalIdentity() {
  currentEmail = null;
}

const TIMEOUT_MS = 8000;

function emptyState() {
  return { seen: {}, lastShownDay: {} };
}

export function readLocalState() {
  try {
    const raw = window.localStorage.getItem(localKey());
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return emptyState();
    return {
      seen: parsed.seen && typeof parsed.seen === 'object' ? parsed.seen : {},
      lastShownDay: parsed.lastShownDay && typeof parsed.lastShownDay === 'object' ? parsed.lastShownDay : {}
    };
  } catch (_) {
    return emptyState();
  }
}

function writeLocalState(state) {
  try {
    window.localStorage.setItem(localKey(), JSON.stringify({ seen: state.seen || {}, lastShownDay: state.lastShownDay || {} }));
  } catch (_) {
    // Storage full / blocked: the server copy still holds it.
  }
}

/** Union of seen ids; the later day per module. */
export function mergeStates(a, b) {
  const left = a || emptyState();
  const right = b || emptyState();
  const lastShownDay = { ...(left.lastShownDay || {}) };
  for (const [module, day] of Object.entries(right.lastShownDay || {})) {
    if (!lastShownDay[module] || String(day) > String(lastShownDay[module])) lastShownDay[module] = day;
  }
  return { seen: { ...(right.seen || {}), ...(left.seen || {}) }, lastShownDay };
}

function config() {
  return { withCredentials: true, timeout: TIMEOUT_MS };
}

export async function fetchWhatsNewState() {
  try {
    const res = await axios.get(`${getApiBaseUrl()}/api/whats-new/state`, config());
    rememberEmail(res.data?.email);
    const merged = mergeStates(readLocalState(), res.data);
    writeLocalState(merged);
    return merged;
  } catch (_) {
    return readLocalState();
  }
}

export async function markWhatsNewSeen(ids, { module, day } = {}) {
  const cleanIds = (ids || []).filter(Boolean).slice(0, 50);
  const local = readLocalState();
  const at = new Date().toISOString();
  const next = mergeStates(local, {
    seen: Object.fromEntries(cleanIds.map((id) => [id, at])),
    lastShownDay: module && day ? { [module]: day } : {}
  });
  writeLocalState(next);
  if (!cleanIds.length) return next;
  try {
    const res = await axios.post(`${getApiBaseUrl()}/api/whats-new/seen`, { ids: cleanIds, ...(module && day ? { module, day } : {}) }, config());
    return mergeStates(next, res.data);
  } catch (_) {
    return next;
  }
}

export async function markWhatsNewShown(module, day) {
  const next = mergeStates(readLocalState(), { seen: {}, lastShownDay: { [module]: day } });
  writeLocalState(next);
  try {
    await axios.post(`${getApiBaseUrl()}/api/whats-new/shown`, { module, day }, config());
  } catch (_) {
    // localStorage already stops a second popup today on this device.
  }
  return next;
}
